/**
 * Renders the FIRST PAGE of a PDF attachment as an inline image, so the
 * attached document appears as a real visual preview in the AEP PDF output.
 *
 * - Loads PDF.js lazily (split chunk) so the bundle stays small.
 * - Fetches the file via the authenticated download endpoint, renders page 1
 *   to an offscreen canvas, then converts to a base64 PNG so the final print
 *   captures the image regardless of whether the network is still alive.
 * - For non-PDF files (Excel, Word), gracefully falls back to a styled
 *   "documento anexado" card with download link.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// PDF.js version pinned in package.json — keep both in sync.
// Using the CDN-hosted worker bypasses bundler quirks where the dynamically
// imported worker module produced by webpack/CRA is served with the wrong
// MIME type in production (causing "Failed to fetch dynamically imported
// module" in nr01sollute.com).
const PDFJS_VERSION = "4.7.76";
const PDFJS_WORKER_SRC = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.mjs`;

let pdfjsPromise = null;
const loadPdfjs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist").then((mod) => {
      mod.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
      return mod;
    });
  }
  return pdfjsPromise;
};

export default function PdfThumbnail({ cnpj, attId, filename, width = 480 }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        const pdfjs = await loadPdfjs();
        const resp = await api.get(
          `/cnpj/${cnpj}/aep-attachments/${attId}/download`,
          { responseType: "arraybuffer" },
        );
        const loadingTask = pdfjs.getDocument({ data: resp.data });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        const scale = width / viewport.width;
        const scaled = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(scaled.width);
        canvas.height = Math.round(scaled.height);
        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport: scaled }).promise;
        setSrc(canvas.toDataURL("image/png"));
      } catch (e) {
        console.error("PdfThumbnail render failed", e);
        setError(e.message || "Não foi possível renderizar o PDF.");
      }
    })();
  }, [cnpj, attId, width]);

  if (error) {
    return (
      <div className="text-xs text-rose-700 italic px-2 py-1" data-testid={`pdf-thumb-error-${attId}`}>
        <i className="fa-solid fa-triangle-exclamation mr-1"></i>
        {error}
      </div>
    );
  }
  if (!src) {
    return (
      <div className="text-xs text-slate-500 italic px-2 py-1 no-print" data-testid={`pdf-thumb-loading-${attId}`}>
        <i className="fa-solid fa-spinner fa-spin mr-1"></i>Gerando preview do PDF…
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={`Página 1 de ${filename}`}
      className="border border-slate-200 rounded shadow-sm"
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
      data-testid={`pdf-thumb-${attId}`}
    />
  );
}
